import React from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FaBell, FaCheckCircle, FaExclamationCircle, FaInfoCircle } from 'react-icons/fa';
import { useNotifications, Notification } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const NotificationItem = ({ notification, onClick }: { notification: Notification; onClick: () => void }) => {
  const getIcon = (type: string) => {
    if (type.includes('success')) return FaCheckCircle;
    if (type.includes('failed') || type.includes('error')) return FaExclamationCircle;
    return FaInfoCircle;
  };

  const getColor = (type: string) => {
    if (type.includes('success')) return 'green.400';
    if (type.includes('failed') || type.includes('error')) return 'red.400';
    return 'blue.400';
  };

  const timeAgo = (timestamp: any) => {
    if (!timestamp || !timestamp._seconds) return '';
    const date = new Date(timestamp._seconds * 1000);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'À l\'instant';
    if (diffInSeconds < 3600) return `Il y a ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Il y a ${Math.floor(diffInSeconds / 3600)} h`;
    return date.toLocaleDateString();
  };

  return (
    <MenuItem 
      onClick={onClick} 
      bg={notification.read ? 'transparent' : 'gray.700'}
      _hover={{ bg: 'gray.600' }}
      borderRadius="md"
      mb={1}
    >
      <HStack align="start" spacing={3} w="100%">
        <Icon as={getIcon(notification.type)} color={getColor(notification.type)} mt={1} />
        <VStack align="start" spacing={0} w="100%">
          <Text fontSize="sm" fontWeight="bold" color="white">
            {notification.title}
          </Text>
          <Text fontSize="xs" color="gray.300" noOfLines={2}>
            {notification.message}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            {timeAgo(notification.createdAt)}
          </Text>
        </VStack>
        {!notification.read && (
          <Box w="8px" h="8px" bg="blue.400" borderRadius="full" mt={2} flexShrink={0} />
        )}
      </HStack>
    </MenuItem>
  );
};

const NotificationCenter = () => {
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: Notification) => {
    markAsRead([notification.id]);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleOpen = () => {
    // Marquer les notifications visibles comme lues (optionnel, ou au clic)
    // Ici on laisse l'utilisateur cliquer pour marquer lu, ou on pourrait faire un "Tout marquer comme lu"
  };

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher la fermeture du menu si souhaité, ou laisser fermer
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length > 0) {
        markAsRead(unreadIds);
    }
  };

  return (
    <Menu onOpen={handleOpen} placement="bottom-end">
      <Box position="relative">
        <MenuButton
          as={IconButton}
          icon={<FaBell />}
          aria-label="Notifications"
        />
        {unreadCount > 0 && (
          <Badge
            position="absolute"
            top="-2px"
            right="-2px"
            fontSize="9px"
            fontWeight="bold"
            bg="red.500"
            color="white"
            borderRadius="full"
            w="16px"
            h="16px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            border="2px solid"
            borderColor="gray.800"
            boxShadow="sm"
            pointerEvents="none"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Box>
      <MenuList 
        bg="gray.800" 
        borderColor="gray.700" 
        maxH="400px" 
        overflowY="auto" 
        w="350px"
        p={2}
        zIndex={100}
      >
        <HStack justify="space-between" px={3} py={2}>
            <Text fontSize="md" fontWeight="bold" color="white">
            Notifications
            </Text>
            {unreadCount > 0 && (
                <Text 
                    fontSize="xs" 
                    color="teal.300" 
                    cursor="pointer" 
                    fontWeight="bold"
                    _hover={{ textDecoration: 'underline', color: 'teal.200' }}
                    onClick={handleMarkAllRead}
                >
                    Tout marquer comme lu
                </Text>
            )}
        </HStack>
        <Divider mb={2} borderColor="gray.600" />
        
        {notifications.length === 0 ? (
          <Box py={8} textAlign="center">
            <Text color="gray.500" fontSize="sm">Aucune notification</Text>
          </Box>
        ) : (
          notifications.map(notif => (
            <NotificationItem 
              key={notif.id} 
              notification={notif} 
              onClick={() => handleNotificationClick(notif)} 
            />
          ))
        )}
      </MenuList>
    </Menu>
  );
};

export default NotificationCenter;
